import { formatMoney, formatNumber } from '@/platform/lib/utils';
import {
    Button,
    Container,
    Form,
    Header,
    Input,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useForm, usePage } from '@inertiajs/react';
import {
    cell,
    customerConversion,
    potentie,
    sumOverYears,
    toInteger,
    withValue,
} from './calculations';
import YearGrid from './components/YearGrid';

const CreateOrUpdateView = () => {
    const {
        auth,
        quickscan,
        entity,
        years,
        target_groups: targetGroups,
        prefill,
    } = usePage().props;

    const base = `/customers/${auth?.customer?.id}/services/quickscans`;
    const isEdit = !!quickscan?.id;
    const ids = Object.keys(targetGroups || {});

    const { data, setData, post, patch, processing } = useForm(
        isEdit
            ? { data: prefill || {} }
            : {
                  customer_entity_id: entity?.id,
                  data: prefill || {},
              }
    );

    const scan = data.data || {};

    const handleChange = (targetGroup, year, field, value) =>
        setData(previous => ({
            ...previous,
            data: withValue(
                previous.data,
                targetGroup,
                year,
                field,
                toInteger(value)
            ),
        }));

    const handleSubmit = () => {
        if (!isEdit) {
            post(base);

            return;
        }

        patch(`${base}/${quickscan.id}`);
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader
                        header='Quickscan LKV'
                        subheader={entity?.name}
                    >
                        <Button as={Link} href={base}>
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent className='space-y-8'>
                        <div className='max-w-xs'>
                            <Input
                                label='Aantal ingeladen XMLs'
                                type='number'
                                required
                                value={scan?.[ids[0]]?.xmls ?? 0}
                                onChange={event =>
                                    handleChange(
                                        ids[0],
                                        null,
                                        'xmls',
                                        event.target.value
                                    )
                                }
                            />
                        </div>

                        <div className='space-y-2'>
                            <Header as='h4'>Nieuwe werknemers</Header>
                            <YearGrid
                                label='Nieuwe werknemers'
                                field='count'
                                years={years}
                                scan={scan}
                                onChange={handleChange}
                            />
                        </div>

                        <div className='space-y-2'>
                            <Header as='h4'>Conversie</Header>
                            <YearGrid
                                label='Nieuwe werknemers toegepast'
                                field='wtl'
                                years={years}
                                scan={scan}
                                onChange={handleChange}
                            >
                                <TableRow>
                                    <TableCell>Conversie Klant</TableCell>
                                    {ids.map(id => (
                                        <TableCell key={id} textAlign='right'>
                                            {formatNumber(
                                                customerConversion(
                                                    scan,
                                                    id,
                                                    years
                                                )
                                            )}
                                            %
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell>Conversie VCSW</TableCell>
                                    {ids.map(id => (
                                        <TableCell key={id} collapsing>
                                            <Input
                                                type='number'
                                                required
                                                aria-label={`Conversie VCSW ${targetGroups[id]}`}
                                                value={
                                                    scan?.[id]?.conversion ?? 0
                                                }
                                                onChange={event =>
                                                    handleChange(
                                                        id,
                                                        null,
                                                        'conversion',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </YearGrid>
                        </div>

                        <div className='space-y-2'>
                            <Header as='h4'>Verloonde uren</Header>
                            <YearGrid
                                label='Verloonde uren'
                                field='hours'
                                years={years}
                                scan={scan}
                                onChange={handleChange}
                            />
                        </div>

                        {years.map(year => (
                            <div key={year} className='space-y-2'>
                                <Header as='h4'>Resultaten {year}</Header>
                                <Table striped>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell>
                                                Doelgroep
                                            </TableHeaderCell>
                                            <TableHeaderCell textAlign='right'>
                                                Toegepast
                                            </TableHeaderCell>
                                            <TableHeaderCell textAlign='right'>
                                                Potentie
                                            </TableHeaderCell>
                                            <TableHeaderCell textAlign='right'>
                                                Resultaat
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {ids.map(id => {
                                            const potential = potentie(
                                                scan,
                                                id,
                                                year
                                            );
                                            const applied = cell(
                                                scan,
                                                id,
                                                year
                                            ).toegepast;

                                            return (
                                                <TableRow key={id}>
                                                    <TableCell>
                                                        {targetGroups[id]}
                                                    </TableCell>
                                                    <TableCell collapsing>
                                                        <Input
                                                            type='number'
                                                            aria-label={`Toegepast ${targetGroups[id]} ${year}`}
                                                            value={applied}
                                                            onChange={event =>
                                                                handleChange(
                                                                    id,
                                                                    year,
                                                                    'toegepast',
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell textAlign='right'>
                                                        {formatMoney(potential)}
                                                    </TableCell>
                                                    <TableCell textAlign='right'>
                                                        {formatMoney(
                                                            potential - applied
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        ))}

                        <div className='space-y-2'>
                            <Header as='h4'>Samenvatting</Header>
                            <Table striped>
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>
                                            Doelgroep
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Toegepast
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Potentie
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Resultaat
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {ids.map(id => {
                                        const applied = sumOverYears(
                                            scan,
                                            id,
                                            years,
                                            'toegepast'
                                        );
                                        const potential = years.reduce(
                                            (total, year) =>
                                                total +
                                                potentie(scan, id, year),
                                            0
                                        );

                                        return (
                                            <TableRow key={id}>
                                                <TableCell>
                                                    {targetGroups[id]}
                                                </TableCell>
                                                <TableCell textAlign='right'>
                                                    {formatMoney(applied)}
                                                </TableCell>
                                                <TableCell textAlign='right'>
                                                    {formatMoney(potential)}
                                                </TableCell>
                                                <TableCell textAlign='right'>
                                                    {formatMoney(
                                                        potential - applied
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </SegmentContent>
                </Segment>

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
